import SpinnerModal from '@components/SpinnerModal';
import {
    Assessment,
    Business,
    CheckCircle,
    Contacts,
    ContentCopy,
    Error
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Typography
} from '@mui/material';
import DataCaptureService from '@services/DataCaptureService';
import SurveyService from '@services/SurveyService';
import React, { Component } from 'react';

import styles from './copyregister.module.css';

class CopyRegister extends Component {
    state = {
        loading: false,
        sectors: [],
        surveyCycles: [],
        sourceCycle: '',
        targetCycle: '',
        orgCopyProgress: 0,
        contactCopyProgress: 0,
        orgCopyStatus: 'idle',
        contactCopyStatus: 'idle',
        orgCopyMessage: '',
        contactCopyMessage: '',
        surveysUserId: null,
        sourceSummary: { organisations: 0, contacts: 0, unitsOfMeasure: 0 },
        targetSummary: { organisations: 0, contacts: 0, unitsOfMeasure: 0 },
        loadingSummary: false,
        sourceSectorSummary: [],
        targetSectorSummary: [],
        selectedSectors: new Set(),
        sectorProgress: {}, 
        unchangedPollCount: 0, // To track consecutive polls with no change
        pollRetryCount: 0,
    }

    NEW_UNCHANGED_POLLING_COUNT = 50;
    POLLING_TIMEOUT = 30000; // 10 sec
    PROGRESS_POLLING_INTERVAL = 2000;
    SUMMARY_POLLING_INTERVAL = 2000;
    MAX_POLL_RETRIES = 3;

    parseJwt(token) {
        const base64Payload = token.split('.')[1];
        const payload = atob(base64Payload);
        return JSON.parse(payload.toString());
    }

    async componentDidMount() {
        this.setState({ loading: true });

        let payload = this.parseJwt(localStorage.getItem('Bearer'));
        this.setState({ surveysUserId: payload.user_id });
        await this.loadSectors();
        await this.loadSurveyCycles();
        this.setState({ loading: false });
    }

    loadSectors = async () => {
        this.setState({ loading: true });

        const sectors = await SurveyService.getAllSectors();
        sectors.forEach(item => {
            if (!this.state.sectors.some(element => element.id === item.id)) {
                this.state.sectors.push(item)
            }
        });

        this.setState({ loading: false });
    }

    loadSurveyCycles = async () => {
        this.setState({ loading: true });
        try {
            const surveyCycles = await SurveyService.getAllSurveyCycles();
            surveyCycles
                .filter(surveyCycle => surveyCycle.status.toUpperCase() === 'ACTIVE')
                .sort((a, b) => a.id > b.id ? -1 : 1)
                .forEach(surveyCycle => {
                    if (!this.state.surveyCycles.some(item => item.id === surveyCycle.id)) {
                        this.state.surveyCycles.push(surveyCycle);
                    }
                });
        } catch (ex) {
            this.setState({
                orgCopyStatus: 'error',
                orgCopyMessage: 'Error loading survey cycles !'
            });
        }
        this.setState({ loading: false });
    }

    handleSourceCycleChange = (event) => {
        const cycleId = event.target.value;
        this.setState({
            sourceCycle: cycleId,
            orgCopyProgress: 0,
            contactCopyProgress: 0,
            orgCopyStatus: 'idle',
            contactCopyStatus: 'idle',
            orgCopyMessage: '',
            contactCopyMessage: '',
            sectorProgress: {},
            selectedSectors: new Set()
        });
        this.loadSummaryData(cycleId, true);
    }

    handleTargetCycleChange = (event) => {
        const cycleId = event.target.value;
        this.setState({
            targetCycle: cycleId,
            orgCopyProgress: 0,
            contactCopyProgress: 0,
            orgCopyStatus: 'idle',
            contactCopyStatus: 'idle',
            orgCopyMessage: '',
            contactCopyMessage: '',
            sectorProgress: {},
            selectedSectors: new Set()
        });
        this.loadSummaryData(cycleId, false);
    }

    copyOrganisations = async () => {
        const { sourceCycle, targetCycle, selectedSectors, sourceSectorSummary } = this.state;

        if (!sourceCycle || !targetCycle) {
            this.setState({
                orgCopyStatus: 'error',
                orgCopyMessage: 'Please select both source and target survey cycles'
            });
            return;
        }

        const selectedSectorArray = Array.from(selectedSectors);

        const copyData = {
            sourceSurveyCycleId: sourceCycle,
            targetSurveyCycleId: targetCycle,
            sectorIds: selectedSectorArray,
            userId: this.state.surveysUserId
        };

        const initialSectorProgress = {};
        selectedSectorArray.forEach(sectorId => {
            initialSectorProgress[sectorId] = {
                orgProgress: 0,
                unitsProgress: 0,
                contactProgress: this.state.sectorProgress[sectorId]?.contactProgress || 0
            };
        });

        this.setState({
            orgCopyStatus: 'copying',
            orgCopyProgress: 0,
            orgCopyMessage: 'Starting organisation copy...',
            sectorProgress: { ...this.state.sectorProgress, ...initialSectorProgress },
            unchangedPollCount: 0
        });

        try {
            await SurveyService.copyRegisterOrganisations(copyData);

            const summaryPollInterval = setInterval(() => {
                this.pollTargetSummary();
            }, this.SUMMARY_POLLING_INTERVAL);

            const progressInterval = setInterval(async () => {
                try {
                    const targetCounts = await DataCaptureService.countAllOrganisationContactsBySurveyCycleIdOrderBySector(targetCycle);

                    let totalProgress = 0;
                    let hasChanges = false;
                    const updatedSectorProgress = { ...this.state.sectorProgress };

                    // Update target sector summary and calculate totals
                    const updatedTargetSectorSummary = this.state.sectors.sort((a, b) => (a.id > b.id ? 1 : -1)).map(sector => {
                        const targetSector = targetCounts.find(t => t.sectorId === sector.id);
                        return {
                            id: sector.id,
                            name: sector.name,
                            organisations: targetSector ? parseInt(targetSector.organisationsCount) || 0 : 0,
                            contacts: targetSector ? parseInt(targetSector.contactCount) || 0 : 0,
                            unitsOfMeasure: targetSector ? parseInt(targetSector.unitsOfMeasureCount) || 0 : 0
                        };
                    });

                    const totalOrgs = updatedTargetSectorSummary.reduce((sum, s) => sum + s.organisations, 0);
                    const totalContacts = updatedTargetSectorSummary.reduce((sum, s) => sum + s.contacts, 0);
                    const totalUnits = updatedTargetSectorSummary.reduce((sum, s) => sum + s.unitsOfMeasure, 0);

                    selectedSectorArray.forEach(sectorId => {
                        const sourceSector = sourceSectorSummary.find(s => s.id === sectorId);
                        const targetSector = targetCounts.find(t => t.sectorId === sectorId);

                        if (sourceSector && updatedSectorProgress[sectorId]) {
                            const sourceOrgs = sourceSector.organisations || 0;
                            const targetOrgs = targetSector ? parseInt(targetSector.organisationsCount) || 0 : 0;
                            const orgProgress = sourceOrgs > 0 ? Math.min((targetOrgs / sourceOrgs) * 100, 100) : 100;

                            const sourceUOMs = sourceSector.unitsOfMeasure || 0;
                            const targetUOMs = targetSector ? parseInt(targetSector.unitsOfMeasureCount) || 0 : 0;
                            const uomProgress = sourceUOMs > 0 ? Math.min((targetUOMs / sourceUOMs) * 100, 100) : 100;

                            if (updatedSectorProgress[sectorId].orgProgress !== orgProgress ||
                                updatedSectorProgress[sectorId].unitsProgress !== uomProgress) {
                                hasChanges = true;
                            }

                            updatedSectorProgress[sectorId].orgProgress = orgProgress;
                            updatedSectorProgress[sectorId].unitsProgress = uomProgress;
                            totalProgress += orgProgress;
                        }
                    });

                    const avgProgress = selectedSectorArray.length > 0 ? totalProgress / selectedSectorArray.length : 0;
                    const newUnchangedCount = hasChanges ? 0 : this.state.unchangedPollCount + 1;

                    this.setState({
                        sectorProgress: updatedSectorProgress,
                        unchangedPollCount: newUnchangedCount,
                        targetSectorSummary: updatedTargetSectorSummary,
                        targetSummary: { organisations: totalOrgs, contacts: totalContacts, unitsOfMeasure: totalUnits }
                    });

                    if (avgProgress >= 100 || newUnchangedCount >= this.NEW_UNCHANGED_POLLING_COUNT) {
                        clearInterval(progressInterval);
                        clearInterval(summaryPollInterval);
                        this.setState({
                            orgCopyStatus: 'completed',
                            orgCopyProgress: 100,
                            orgCopyMessage: 'Organisations copied successfully!'
                        });
                    }
                } catch (pollError) {
                    const newRetryCount = (this.state.pollRetryCount || 0) + 1;
                    this.setState({ pollRetryCount: newRetryCount });

                    if (newRetryCount >= 3) {
                        clearInterval(progressInterval);
                        clearInterval(summaryPollInterval);
                        this.setState({
                            orgCopyStatus: 'error',
                            orgCopyMessage: 'Connection error during copy process. Please refresh and check status.'
                        });
                    }
                }
            }, this.PROGRESS_POLLING_INTERVAL);

        } catch (error) {
            this.setState({
                orgCopyStatus: 'error',
                orgCopyMessage: 'Error copying organisations: ' + error.message
            });
        }
    }

    copyContacts = async () => {
        const { sourceCycle, targetCycle, selectedSectors, sourceSectorSummary } = this.state;

        if (!sourceCycle || !targetCycle) {
            this.setState({
                contactCopyStatus: 'error',
                contactCopyMessage: 'Please select both source and target survey cycles'
            });
            return;
        }

        const selectedSectorArray = Array.from(selectedSectors);

        const copyData = {
            sourceSurveyCycleId: sourceCycle,
            targetSurveyCycleId: targetCycle,
            sectorIds: selectedSectorArray,
            userId: this.state.surveysUserId
        };

        this.setState({
            contactCopyStatus: 'copying',
            contactCopyProgress: 0,
            contactCopyMessage: 'Starting contact copy...',
            unchangedPollCount: 0
        });

        try {
            await SurveyService.copyRegisterOrganisationContacts(copyData);

            const summaryPollInterval = setInterval(() => {
                this.pollTargetSummary();
            }, this.SUMMARY_POLLING_INTERVAL);

            const progressInterval = setInterval(async () => {
                try {
                    const targetCounts = await DataCaptureService.countAllOrganisationContactsBySurveyCycleIdOrderBySector(targetCycle);

                    let totalProgress = 0;
                    let hasChanges = false;
                    const updatedSectorProgress = { ...this.state.sectorProgress };

                    // Update target sector summary and calculate totals
                    const updatedTargetSectorSummary = this.state.sectors.sort((a, b) => (a.id > b.id ? 1 : -1)).map(sector => {
                        const targetSector = targetCounts.find(t => t.sectorId === sector.id);
                        return {
                            id: sector.id,
                            name: sector.name,
                            organisations: targetSector ? parseInt(targetSector.organisationsCount) || 0 : 0,
                            contacts: targetSector ? parseInt(targetSector.contactCount) || 0 : 0,
                            unitsOfMeasure: targetSector ? parseInt(targetSector.unitsOfMeasureCount) || 0 : 0
                        };
                    });

                    const totalOrgs = updatedTargetSectorSummary.reduce((sum, s) => sum + s.organisations, 0);
                    const totalContacts = updatedTargetSectorSummary.reduce((sum, s) => sum + s.contacts, 0);
                    const totalUnits = updatedTargetSectorSummary.reduce((sum, s) => sum + s.unitsOfMeasure, 0);

                    selectedSectorArray.forEach(sectorId => {
                        const sourceSector = sourceSectorSummary.find(s => s.id === sectorId);
                        const targetSector = targetCounts.find(t => t.sectorId === sectorId);

                        if (sourceSector && updatedSectorProgress[sectorId]) {
                            const sourceContacts = sourceSector.contacts || 0;
                            const targetContacts = targetSector ? parseInt(targetSector.contactCount) || 0 : 0;
                            const contactProgress = sourceContacts > 0 ? Math.min((targetContacts / sourceContacts) * 100, 100) : 100;

                            const sourceUOMs = sourceSector.unitsOfMeasure || 0;
                            const targetUOMs = targetSector ? parseInt(targetSector.unitsOfMeasureCount) || 0 : 0;
                            const uomProgress = sourceUOMs > 0 ? Math.min((targetUOMs / sourceUOMs) * 100, 100) : 100;

                            if (updatedSectorProgress[sectorId].contactProgress !== contactProgress ||
                                updatedSectorProgress[sectorId].unitsProgress !== uomProgress) {
                                hasChanges = true;
                            }

                            updatedSectorProgress[sectorId].contactProgress = contactProgress;
                            updatedSectorProgress[sectorId].unitsProgress = uomProgress;
                            totalProgress += contactProgress;
                        }
                    });

                    const avgProgress = selectedSectorArray.length > 0 ? totalProgress / selectedSectorArray.length : 0;
                    const newUnchangedCount = hasChanges ? 0 : this.state.unchangedPollCount + 1;

                    this.setState({
                        contactCopyProgress: avgProgress,
                        contactCopyMessage: `Copying contacts... ${Math.round(avgProgress)}%`,
                        sectorProgress: updatedSectorProgress,
                        unchangedPollCount: newUnchangedCount,
                        targetSectorSummary: updatedTargetSectorSummary,
                        targetSummary: { organisations: totalOrgs, contacts: totalContacts, unitsOfMeasure: totalUnits }
                    });

                    if (avgProgress >= 100 || newUnchangedCount >= this.NEW_UNCHANGED_POLLING_COUNT) {
                        clearInterval(progressInterval);
                        clearInterval(summaryPollInterval);
                        this.setState({
                            contactCopyStatus: 'completed',
                            contactCopyProgress: 100,
                            contactCopyMessage: 'Contacts copied successfully!'
                        });
                    }
                } catch (pollError) {
                    const newRetryCount = (this.state.pollRetryCount || 0) + 1;
                    this.setState({ pollRetryCount: newRetryCount });

                    if (newRetryCount >= 3) {
                        clearInterval(progressInterval);
                        clearInterval(summaryPollInterval);
                        this.setState({
                            contactCopyStatus: 'error',  
                            contactCopyMessage: 'Connection error during copy process. Please refresh and check status.'
                        });
                    }
                }
            }, this.PROGRESS_POLLING_INTERVAL);

        } catch (error) {
            this.setState({
                contactCopyStatus: 'error',
                contactCopyMessage: 'Error copying contacts: ' + error.message
            });
        }
    }

    resetCopyStatus = () => {
        this.setState({
            orgCopyProgress: 0,
            contactCopyProgress: 0,
            orgCopyStatus: 'idle',
            contactCopyStatus: 'idle',
            orgCopyMessage: '',
            contactCopyMessage: ''
        });
    }

    loadSummaryData = async (cycleId, isSource = true) => {
        if (!cycleId) return;

        this.setState({ loadingSummary: true });
        try {
            // Simulate sector data
            const cycleCounts = await DataCaptureService.countAllOrganisationContactsBySurveyCycleIdOrderBySector(cycleId);

            const sectors = this.state.sectors.sort((a, b) => (a.id > b.id ? 1 : -1)).map(sector => {
                return {
                    id: sector.id,
                    name: sector.name,
                    organisations: (cycleCounts.length > 0 ? parseInt(cycleCounts.filter(item => item.sectorId === sector.id)[0]?.organisationsCount) : 0),
                    contacts: (cycleCounts.length > 0 ? parseInt(cycleCounts.filter(item => item.sectorId === sector.id)[0]?.contactCount) : 0),
                    unitsOfMeasure: (cycleCounts.length > 0 ? parseInt(cycleCounts.filter(item => item.sectorId === sector.id)[0]?.unitsOfMeasureCount) : 0)
                };
            });

            const totalOrgs = sectors.reduce((sum, s) => sum + s.organisations, 0);
            const totalContacts = sectors.reduce((sum, s) => sum + s.contacts, 0);
            const totalUnits = sectors.reduce((sum, s) => sum + s.unitsOfMeasure, 0);

            if (isSource) {
                this.setState({
                    sourceSummary: { organisations: totalOrgs, contacts: totalContacts, unitsOfMeasure: totalUnits },
                    sourceSectorSummary: sectors
                });
            } else {
                this.setState({
                    targetSummary: { organisations: totalOrgs, contacts: totalContacts, unitsOfMeasure: totalUnits },
                    targetSectorSummary: sectors
                });
            }

        } catch (error) {
            console.error('Error loading summary data:', error);
        }
        this.setState({ loadingSummary: false });
    }

    // Helper function to check if a sector has existing data
    hasExistingData = (sectorId) => {
        const { targetSectorSummary } = this.state;
        const targetSector = targetSectorSummary.find(s => s.id === sectorId);
        return targetSector && (targetSector.organisations > 0 || targetSector.contacts > 0);
    }

    pollTargetSummary = async () => {
        if (!this.state.targetCycle) return;
        
        try {
            const targetCounts = await DataCaptureService.countAllOrganisationContactsBySurveyCycleIdOrderBySector(this.state.targetCycle);
            const { sourceSectorSummary, selectedSectors } = this.state;
            const selectedSectorArray = Array.from(selectedSectors);
            
            let hasChanges = false;
            const updatedSectorProgress = { ...this.state.sectorProgress };
            
            selectedSectorArray.forEach(sectorId => {
                const sourceSector = sourceSectorSummary.find(s => s.id === sectorId);
                const targetSector = targetCounts.find(t => t.sectorId === sectorId);
                
                if (sourceSector && updatedSectorProgress[sectorId]) {
                    // Calculate org progress
                    const sourceOrgs = sourceSector.organisations || 0;
                    const targetOrgs = targetSector ? parseInt(targetSector.organisationsCount) || 0 : 0;
                    const orgProgress = sourceOrgs > 0 ? Math.min((targetOrgs / sourceOrgs) * 100, 100) : 100;
                    
                    // Calculate UOM progress
                    const sourceUOMs = sourceSector.unitsOfMeasure || 0;
                    const targetUOMs = targetSector ? parseInt(targetSector.unitsOfMeasureCount) || 0 : 0;
                    const uomProgress = sourceUOMs > 0 ? Math.min((targetUOMs / sourceUOMs) * 100, 100) : 100;
                    
                    // Calculate contact progress
                    const sourceContacts = sourceSector.contacts || 0;
                    const targetContacts = targetSector ? parseInt(targetSector.contactCount) || 0 : 0;
                    const contactProgress = sourceContacts > 0 ? Math.min((targetContacts / sourceContacts) * 100, 100) : 100;
                    
                    // Check for changes
                    if (updatedSectorProgress[sectorId].orgProgress !== orgProgress ||
                        updatedSectorProgress[sectorId].unitsProgress !== uomProgress ||
                        updatedSectorProgress[sectorId].contactProgress !== contactProgress) {
                        
                        updatedSectorProgress[sectorId].orgProgress = orgProgress;
                        updatedSectorProgress[sectorId].unitsProgress = uomProgress;
                        updatedSectorProgress[sectorId].contactProgress = contactProgress;
                        hasChanges = true;
                    }
                }
            });
            
            // Only update state if there are actual changes
            if (hasChanges) {
                this.setState({ sectorProgress: updatedSectorProgress });
            }
            
        } catch (error) {
            console.error('Error polling target summary:', error);
        }
    }

    // Helper function to check if target has existing organisations for selected sectors
    hasTargetOrganisations = () => {
        const { selectedSectors, targetSectorSummary } = this.state;
        return Array.from(selectedSectors).some(sectorId => {
            const targetSector = targetSectorSummary.find(s => s.id === sectorId);
            return targetSector && targetSector.organisations > 0;
        });
    }

    // Helper function to check if target has existing contacts for selected sectors
    hasTargetContacts = () => {
        const { selectedSectors, targetSectorSummary } = this.state;
        return Array.from(selectedSectors).some(sectorId => {
            const targetSector = targetSectorSummary.find(s => s.id === sectorId);
            return targetSector && targetSector.contacts > 0;
        });
    }

    // Helper function to check if both organisations and contacts exist
    hasBothOrgsAndContacts = (sectorId) => {
        const { targetSectorSummary } = this.state;
        const targetSector = targetSectorSummary.find(s => s.id === sectorId);
        return targetSector && targetSector.organisations > 0 && targetSector.contacts > 0;
    }

    handleSectorToggle = (sectorId) => {
        const newSelected = new Set(this.state.selectedSectors);
        
        if (newSelected.has(sectorId)) {
            newSelected.delete(sectorId);
            // Clear any existing warning when deselecting
            if (this.state.orgCopyStatus === 'error' && this.state.orgCopyMessage.includes('already contains copied data')) {
                this.setState({
                    orgCopyStatus: 'idle',
                    orgCopyMessage: ''
                });
            }
        } else {
            // Check if both organisations and contacts exist before adding
            if (this.hasBothOrgsAndContacts(sectorId)) {
                this.setState({
                    orgCopyStatus: 'error',
                    orgCopyMessage: 'The selected sector already contains copied data. Please consult system administrator before attempting to proceed.'
                });
                return; // Don't add the sector
            }
            newSelected.add(sectorId);
        }
        
        this.setState({ selectedSectors: newSelected });
    }


    render() {
        const {
            loading,
            surveyCycles,
            sourceCycle,
            targetCycle,
            orgCopyProgress,
            contactCopyProgress,
            orgCopyStatus,
            contactCopyStatus,
            orgCopyMessage,
            contactCopyMessage,
            sourceSummary,
            targetSummary,
            loadingSummary,
            sourceSectorSummary,
            targetSectorSummary,
            sectorProgress
        } = this.state;


        if (loading) {
            return <SpinnerModal settings={true} label={'Loading survey cycles...'} />
        }

        const getSelectedCycleName = (cycleId) => {
            const cycle = surveyCycles.find(c => c.id === cycleId);
            return cycle ? cycle.name : '';
        };

        return (
            <div className={styles.container_copyregister}>
                <div className={styles.featuredItem}>
                    <Typography variant="h4" className={styles.featuredTitleHeader}>
                        <ContentCopy sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Register Copy
                    </Typography>
                    <hr />

                    <Grid container spacing={3}>
                        {/* Status Alerts */}
                        {(orgCopyStatus === 'error' || contactCopyStatus === 'error') && (
                            <Grid item xs={12}>
                                <Alert severity="error">
                                    {orgCopyStatus === 'error' && contactCopyStatus === 'error'
                                        ? `${orgCopyMessage} ${contactCopyMessage}`
                                        : orgCopyStatus === 'error'
                                            ? orgCopyMessage
                                            : contactCopyMessage
                                    }
                                </Alert>
                            </Grid>
                        )}
                        {(sourceCycle && targetCycle && sourceCycle === targetCycle) && (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    Source and target survey cycles cannot be the same. Please select different cycles.
                                </Alert>
                            </Grid>
                        )}
                        {orgCopyStatus === 'completed' && contactCopyStatus !== 'completed' && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    Organisation copying completed successfully! You can now proceed to copy contacts.
                                </Alert>
                            </Grid>
                        )}
                        {contactCopyStatus === 'completed' && orgCopyStatus !== 'completed' && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    Contact copying completed successfully!
                                </Alert>
                            </Grid>
                        )}
                        {(orgCopyStatus === 'completed' && contactCopyStatus === 'completed') && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    All organisations and contacts has successfully copied from the source to target survey cycle.
                                </Alert>
                            </Grid>
                        )}
                        {(orgCopyStatus === 'copying' || contactCopyStatus === 'copying') && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    {orgCopyStatus === 'copying' && contactCopyStatus === 'copying'
                                        ? `${orgCopyMessage} ${contactCopyMessage}`
                                        : orgCopyStatus === 'copying'
                                            ? orgCopyMessage
                                            : contactCopyMessage
                                    }
                                </Alert>
                            </Grid>
                        )}


                        {/* Survey Cycle Selection */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Select Source And Target Survey Cycles
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Source Survey Cycle</InputLabel>
                                                <Select
                                                    value={sourceCycle}
                                                    onChange={this.handleSourceCycleChange}
                                                    label="Source Survey Cycle"
                                                >
                                                    {surveyCycles.map((cycle) => (
                                                        <MenuItem key={cycle.id} value={cycle.id}>
                                                            {cycle.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Target Survey Cycle</InputLabel>
                                                <Select
                                                    value={targetCycle}
                                                    onChange={this.handleTargetCycleChange}
                                                    label="Target Survey Cycle"
                                                >
                                                    {surveyCycles.map((cycle) => (
                                                        <MenuItem key={cycle.id} value={cycle.id}>
                                                            {cycle.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Summary Section */}
                        {(sourceCycle || targetCycle) && (
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Register Summary
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {sourceCycle && (
                                                <Grid item xs={6}>
                                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                        <Typography variant="subtitle1" gutterBottom>
                                                            Source: {getSelectedCycleName(sourceCycle)}
                                                        </Typography>
                                                        {loadingSummary ? (
                                                            <Typography variant="body2">Loading...</Typography>
                                                        ) : (
                                                            <Box>
                                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                                    <Chip
                                                                        icon={<Business />}
                                                                        label={`${isNaN(sourceSummary.organisations) ? 0 : sourceSummary.organisations} Organisations`}
                                                                        color="primary"
                                                                        variant="outlined"
                                                                    />
                                                                    <Chip
                                                                        icon={<Assessment />}
                                                                        label={`${isNaN(sourceSummary.unitsOfMeasure) ? 0 : sourceSummary.unitsOfMeasure} UOMs`}
                                                                        color="info"
                                                                        variant="outlined"
                                                                    />
                                                                    <Chip
                                                                        icon={<Contacts />}
                                                                        label={`${isNaN(sourceSummary.contacts) ? 0 : sourceSummary.contacts} Contacts`}
                                                                        color="secondary"
                                                                        variant="outlined"
                                                                    />
                                                                </Box>

                                                                {sourceSectorSummary.map(sector => (
                                                                    <Box key={sector.id} sx={{ mb: 1 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                            <Checkbox
                                                                                checked={this.state.selectedSectors.has(sector.id)}
                                                                                onChange={() => this.handleSectorToggle(sector.id)}
                                                                                size="small"
                                                                            />
                                                                            <Typography variant="body2" sx={{ minWidth: 80 }}>
                                                                                {sector.name}:
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                                                                                <Chip size="small" label={`${sector.organisations} Orgs`} sx={{ mb: 0.5 }} />
                                                                                {(orgCopyStatus === 'copying' || orgCopyStatus === 'completed' || contactCopyStatus === 'copying' || contactCopyStatus === 'completed') && this.state.selectedSectors.has(sector.id) && (
                                                                                    <LinearProgress
                                                                                        variant="determinate"
                                                                                        value={sectorProgress[sector.id]?.orgProgress || 0}
                                                                                        sx={{ width: '100%', height: 4 }}
                                                                                        color="primary"
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                                                                                <Chip size="small" label={`${sector.unitsOfMeasure} UOMs`} sx={{ mb: 0.5 }} />
                                                                                {(orgCopyStatus === 'copying' || orgCopyStatus === 'completed' || contactCopyStatus === 'copying' || contactCopyStatus === 'completed') && this.state.selectedSectors.has(sector.id) && (
                                                                                    <LinearProgress
                                                                                        variant="determinate"
                                                                                        value={sectorProgress[sector.id]?.unitsProgress || 0}
                                                                                        sx={{ width: '100%', height: 4 }}
                                                                                        color="info"
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                                <Chip size="small" label={`${sector.contacts} Contacts`} sx={{ mb: 0.5 }} />
                                                                                {(orgCopyStatus === 'copying' || orgCopyStatus === 'completed' || contactCopyStatus === 'copying' || contactCopyStatus === 'completed') && this.state.selectedSectors.has(sector.id) && (
                                                                                    <LinearProgress
                                                                                        variant="determinate"
                                                                                        value={sectorProgress[sector.id]?.contactProgress || 0}
                                                                                        sx={{ width: '100%', height: 4 }}
                                                                                        color="secondary"
                                                                                    />
                                                                                )}
                                                                            </Box>

                                                                        </Box>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            )}
                                            {targetCycle && (
                                                <Grid item xs={6}>
                                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                        <Typography variant="subtitle1" gutterBottom>
                                                            Target: {getSelectedCycleName(targetCycle)}
                                                        </Typography>
                                                        {loadingSummary ? (
                                                            <Typography variant="body2">Loading...</Typography>
                                                        ) : (
                                                            <Box>
                                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                                    <Chip
                                                                        icon={<Business />}
                                                                        label={`${isNaN(targetSummary.organisations) ? 0 : targetSummary.organisations} Organisations`}
                                                                        color="primary"
                                                                        variant="outlined"
                                                                    />
                                                                    <Chip
                                                                        icon={<Assessment />}
                                                                        label={`${isNaN(targetSummary.unitsOfMeasure) ? 0 : targetSummary.unitsOfMeasure} UOMs`}
                                                                        color="info"
                                                                        variant="outlined"
                                                                    />
                                                                    <Chip
                                                                        icon={<Contacts />}
                                                                        label={`${isNaN(targetSummary.contacts) ? 0 : targetSummary.contacts} Contacts`}
                                                                        color="secondary"
                                                                        variant="outlined"
                                                                    />
                                                                </Box>
                                                                {targetSectorSummary.map(sector => (
                                                                    <Box key={sector.id} sx={{ mb: 1 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                            <Box sx={{ width: 42 }} /> {/* Space for checkbox alignment */}
                                                                            <Typography variant="body2" sx={{ minWidth: 80 }}>
                                                                                {sector.name}:
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={`${isNaN(sector.organisations) ? 0 : sector.organisations} Orgs`}
                                                                                    sx={{ mb: 0.5 }}
                                                                                />
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={`${isNaN(sector.unitsOfMeasure) ? 0 : sector.unitsOfMeasure} UOMs`}
                                                                                    sx={{ mb: 0.5 }}
                                                                                />
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={`${isNaN(sector.contacts) ? 0 : sector.contacts} Contacts`}
                                                                                    sx={{ mb: 0.5 }}
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                ))}

                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Organisation Copy Section */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Copy Organisations
                                    </Typography>

                                    {orgCopyStatus !== 'idle' && (
                                        <Box sx={{ mb: 2 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={orgCopyProgress}
                                                color={orgCopyStatus === 'error' ? 'error' : 'primary'}
                                            />
                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                {orgCopyStatus === 'completed' && <CheckCircle color="success" sx={{ mr: 1 }} />}
                                                {orgCopyStatus === 'error' && <Error color="error" sx={{ mr: 1 }} />}
                                                <Typography variant="body2">{orgCopyMessage}</Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Copy Organisations Button */}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={this.copyOrganisations}
                                        disabled={
                                            orgCopyStatus === 'copying' ||
                                            !sourceCycle ||
                                            !targetCycle ||
                                            sourceCycle === targetCycle ||
                                            this.state.selectedSectors.size === 0 ||
                                            this.hasTargetOrganisations()
                                        }
                                        startIcon={<Business />}
                                        fullWidth
                                    >
                                        {orgCopyStatus === 'copying' ? 'Copying...' : 'Copy Organisations'}
                                    </Button>


                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Contact Copy Section */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        <Contacts sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Copy Contacts
                                    </Typography>

                                    {contactCopyStatus !== 'idle' && (
                                        <Box sx={{ mb: 2 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={contactCopyProgress}
                                                color={contactCopyStatus === 'error' ? 'error' : 'primary'}
                                            />
                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                {contactCopyStatus === 'completed' && <CheckCircle color="success" sx={{ mr: 1 }} />}
                                                {contactCopyStatus === 'error' && <Error color="error" sx={{ mr: 1 }} />}
                                                <Typography variant="body2">{contactCopyMessage}</Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Copy Contacts Button */}
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={this.copyContacts}
                                        disabled={
                                            contactCopyStatus === 'copying' ||
                                            !sourceCycle ||
                                            !targetCycle ||
                                            sourceCycle === targetCycle ||
                                            this.state.selectedSectors.size === 0 ||
                                            this.hasTargetContacts()
                                        }
                                        startIcon={<Contacts />}
                                        fullWidth
                                    >
                                        {contactCopyStatus === 'copying' ? 'Copying...' : 'Copy Contacts'}
                                    </Button>

                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Reset Button */}
                        {(orgCopyStatus !== 'idle' || contactCopyStatus !== 'idle') && (
                            <Grid item xs={12}>
                                <Button
                                    variant="outlined"
                                    onClick={this.resetCopyStatus}
                                    disabled={orgCopyStatus === 'copying' || contactCopyStatus === 'copying'}
                                >
                                    Reset Status
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </div>
            </div>
        );
    }
}

export default CopyRegister;
